import React, { Fragment, useMemo, useState } from "react";
import Select from "react-select";
import { NumericFormat } from "react-number-format";
import {
  TrashIcon,
  PlusIcon,
  InformationCircleIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import ErrorSelect from "../../ErrorSelect";
import FormLabel from "../../FormLabel";
import clsx from "clsx";
import { currencyFormat } from "../../../Utils";

const JournalEntryTable = ({
  accountItems,
  setaccountItems,
  accounts,
  debitError,
  allAccountLoading,
  inputErrors,
  isFormSubmit,
  updateAccountItem,
  handleRemoveRow,
  handleAddRow,
  selectedBusiness,
  businessIdParam,
  accountingDispatch,
  requestListAccount,
  accountItemsError,
  editModal = false,
}) => {
  const { t } = useTranslation();

  const [expandedEntries, setExpandedEntries] = useState([]);

  const isMaxJournalReached = accountItems.length >= 5;

  const duplicateAccounts = useMemo(() => {
    const accountIds = accountItems
      .filter((item) => item.account && item.account.value)
      .map((item) => item.account.value);

    const duplicates = accountIds.filter(
      (id, index) => accountIds.indexOf(id) !== index
    );

    return duplicates;
  }, [accountItems]);

  const getEntrySummary = (item) => {
    const accountName =
      item.account && item.account.label
        ? item.account.label
        : t("transactions.table.errors.noSelected");

    const debitAmount =
      item.debit && item.debit.floatValue ? item.debit.value : "0";

    const creditAmount =
      item.credit && item.credit.floatValue ? item.credit.value : "0";

    return { accountName, debitAmount, creditAmount };
  };

  // Create a modified handleAddRow function that will also collapse previous row
  const handleAddRowWithCollapse = () => {
    // Don't add if we've reached the maximum
    if (isMaxJournalReached) return;

    // Collapse the last product (previous last row)
    if (accountItems.length > 0) {
      const lastIndex = accountItems.length - 1;
      if (!expandedEntries.includes(lastIndex)) {
        setExpandedEntries((prev) => [...prev, lastIndex]);
      }
    }

    // Call original handleAddRow
    handleAddRow();
  };

  const toggleExpand = (index) => {
    setExpandedEntries((prevState) => {
      if (prevState.includes(index)) {
        return prevState.filter((item) => item !== index);
      } else {
        return [...prevState, index];
      }
    });
  };

  // This is our fixed version of the remove row handler that also updates collapsed state
  const handleRemoveRowWithCollapseFix = (index) => {
    // First remove the row through the original handler
    handleRemoveRow(index);

    // Then update our collapsed items state
    setExpandedEntries((prevState) => {
      // Remove the collapsed state for the deleted item
      const newState = prevState.filter((item) => item !== index);

      // Shift down indexes of items that come after the deleted item
      return newState.map((item) => {
        if (item > index) {
          return item - 1;
        }
        return item;
      });
    });
  };

  const isExpanded = (index) => expandedEntries.includes(index);

  return (
    <div className="flex flex-col w-full">
      <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
        {accountItems?.map((item, index) => {
          const lastIndex = accountItems.length - 1;
          const isAccountValid = item.account?.value;
          const isMinimumAccountValid = accountItems.length >= 2;
          const isDebitValid = item.debit.floatValue !== undefined;
          const isCreditValid = item.credit.floatValue !== undefined;
          const isRowValid =
            (item.debit.floatValue !== 0 && item.credit.floatValue === 0) ||
            (item.debit.floatValue === 0 && item.credit.floatValue !== 0);
          const isDuplicateAccount =
            item.account &&
            item.account.value &&
            duplicateAccounts.includes(item.account.value);
          const { accountName, debitAmount, creditAmount } =
            getEntrySummary(item);
          const rowError =
            !isAccountValid ||
            !isDebitValid ||
            !isCreditValid ||
            !isRowValid ||
            isDuplicateAccount;
          const hasError =
            rowError ||
            (inputErrors?.debitCredit && !isRowValid) ||
            (inputErrors?.accountItems && !isAccountValid) ||
            (inputErrors?.debitAmount && !isDebitValid) ||
            (inputErrors?.creditAmount && !isCreditValid) ||
            isDuplicateAccount;

          return (
            <Fragment key={index}>
              <div className="p-4 border-b border-gray-200">
                <div className="flex justify-between items-start">
                  <div className="flex-grow">
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() => toggleExpand(index)}
                        className="mr-2 text-gray-500 hover:text-gray-700"
                        aria-expanded={!isExpanded(index)}
                        aria-label={
                          isExpanded(index)
                            ? "Expand Journal Entry"
                            : "Collapse Journal Entry"
                        }
                      >
                        {!isExpanded(index) ? (
                          <ChevronDownIcon className="h-5 w-5" />
                        ) : (
                          <ChevronRightIcon className="h-5 w-5" />
                        )}
                      </button>
                      <h3 className="text-sm font-medium text-gray-900 mr-4">
                        {accountName}
                      </h3>
                    </div>
                    <div className="flex items-center">
                      {isExpanded(index) && (
                        <div className="mt-3 mr-3 text-xs font-medium text-gray-900 space-y-2">
                          {debitAmount === "0" && creditAmount === "0" && (
                            <div className="flex items-center text-xs font-medium text-gray-900 gap-2">
                              <div className="bg-blue-50 px-3 py-1 rounded-full flex items-center ">
                                <span>
                                  {t("transactions.table.headers.debit")}{" "}
                                  {currencyFormat(debitAmount)}
                                </span>
                              </div>
                              <div className="bg-blue-50 px-3 py-1 rounded-full flex items-center">
                                <span>
                                  {t("transactions.table.headers.debit")}{" "}
                                  {currencyFormat(creditAmount)}
                                </span>
                              </div>
                            </div>
                          )}
                          {debitAmount !== "0" && (
                            <div className="bg-blue-50 px-3 py-1 w-fit rounded-full flex items-center text-xs font-medium text-gray-900">
                              <span>
                                {t("transactions.table.headers.debit")}{" "}
                                {currencyFormat(debitAmount)}
                              </span>
                            </div>
                          )}
                          {creditAmount !== "0" && (
                            <div className="bg-blue-50 px-3 py-1 w-fit rounded-full flex items-center text-xs font-medium text-gray-900">
                              <span>
                                {t("transactions.table.headers.credit")}{" "}
                                {currencyFormat(creditAmount)}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {hasError && isFormSubmit.current && isExpanded(index) && (
                      <span className="text-xs text-red-600">
                        <InformationCircleIcon className="h-6 w-6" />
                      </span>
                    )}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleRemoveRowWithCollapseFix(index);
                      }}
                      className="flex items-center text-sm px-3 py-1"
                      disabled={accountItems.length === 1}
                    >
                      <TrashIcon
                        className={`h-5 w-5 text-${
                          accountItems.length === 1
                            ? "gray-400 cursor-not-allowed"
                            : "red-500 cursor-pointer"
                        } `}
                      />
                    </button>
                  </div>
                </div>

                {!isExpanded(index) && (
                  <div>
                    <div className="mb-4">
                      <FormLabel
                        htmlFor={`account-${index}`}
                        label={t("transactions.table.headers.account")}
                        required
                      />
                      {debitError ? (
                        <ErrorSelect
                          name="account"
                          customClassNames="mt-1"
                          error={debitError}
                          onRefresh={() => {
                            if (businessIdParam || selectedBusiness.id) {
                              requestListAccount(accountingDispatch, {
                                businessID:
                                  selectedBusiness.id || businessIdParam,
                                loadAll: true,
                              });
                            }
                          }}
                        />
                      ) : (
                        <Select
                          // inputId (not id) lands on the focusable input, so
                          // the label above names the picker for assistive tech.
                          inputId={`account-${index}`}
                          className={clsx(
                            (!isAccountValid || isDuplicateAccount) &&
                              isFormSubmit.current
                              ? "border-red-500 rounded border focus:border-red-500 focus:ring-red-500"
                              : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                            "text-sm mt-1"
                          )}
                          classNamePrefix="select2-selection"
                          options={accounts.slice().map((obj) => ({
                            ...(editModal && obj),
                            value: obj.id,
                            label: `${obj.code} - ${obj.name}`,
                          }))}
                          value={item?.account || ""}
                          onChange={(selectedOption) =>
                            setaccountItems(
                              updateAccountItem(index, {
                                account: selectedOption,
                              })
                            )
                          }
                          placeholder={t(
                            "transactions.table.placeholders.account"
                          )}
                          maxMenuHeight={150}
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                          }}
                          menuPortalTarget={!editModal ? document.body : null}
                          isDisabled={allAccountLoading}
                          isLoading={allAccountLoading}
                        />
                      )}
                      {inputErrors?.accountItems && !isAccountValid && (
                        <p className="mt-1 text-sm text-red-600">
                          {t(inputErrors.accountItems)}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor={`debit-${index}`}
                          className="block text-sm font-medium text-gray-700"
                        >
                          {t("transactions.table.headers.debit")}
                        </label>
                        <NumericFormat
                          type="text"
                          name={`debit-${index}`}
                          id={`debit-${index}`}
                          value={item.debit?.value || ""}
                          placeholder={t(
                            "transactions.table.placeholders.amount"
                          )}
                          thousandSeparator="."
                          decimalSeparator=","
                          displayType="input"
                          prefix={"Rp"}
                          onValueChange={(values) => {
                            setaccountItems(
                              updateAccountItem(index, { debit: values })
                            );
                          }}
                          className={clsx(
                            !isDebitValid ||
                              (!isRowValid && isFormSubmit.current)
                              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                              : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                            "block w-full text-sm rounded-md shadow-sm mt-1"
                          )}
                        />
                        {inputErrors?.debitAmount && !isDebitValid && (
                          <p className="mt-1 text-sm text-red-600">
                            {t(inputErrors.debitAmount)}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor={`credit-${index}`}
                          className="block text-sm font-medium text-gray-700"
                        >
                          {t("transactions.table.headers.credit")}
                        </label>
                        <NumericFormat
                          type="text"
                          name={`credit-${index}`}
                          id={`credit-${index}`}
                          value={item.credit?.value || ""}
                          placeholder={t(
                            "transactions.table.placeholders.amount"
                          )}
                          thousandSeparator="."
                          decimalSeparator=","
                          displayType="input"
                          prefix={"Rp"}
                          onValueChange={(values) => {
                            setaccountItems(
                              updateAccountItem(index, { credit: values })
                            );
                          }}
                          className={clsx(
                            !isCreditValid ||
                              (!isRowValid && isFormSubmit.current)
                              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                              : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                            "block w-full text-sm rounded-md shadow-sm mt-1"
                          )}
                        />
                        {inputErrors?.creditAmount && !isCreditValid && (
                          <p className="mt-1 text-sm text-red-600">
                            {t(inputErrors.creditAmount)}
                          </p>
                        )}
                      </div>
                    </div>
                    {inputErrors?.debitCredit && !isRowValid && (
                      <p className="mt-2 text-sm text-red-600">
                        {t(inputErrors.debitCredit)}
                      </p>
                    )}

                    {!isMinimumAccountValid &&
                      isFormSubmit.current &&
                      index === lastIndex && (
                        <p className="mt-2 text-sm text-red-600">
                          {t("transactions.table.errors.minimumJournal")}
                        </p>
                      )}
                  </div>
                )}
              </div>
            </Fragment>
          );
        })}

        {accountItemsError && (
          <div className="px-4 py-2 border-b border-gray-200">
            <p className="text-sm text-red-600">{t(accountItemsError)}</p>
          </div>
        )}
        {duplicateAccounts.length > 0 && (
          <div className="px-4 py-2 border-b border-gray-200">
            <p className="text-sm text-red-600">
              {t("transactions.table.errors.duplicateAccounts")}
            </p>
          </div>
        )}

        <div className="p-4">
          <button
            type="button"
            onClick={handleAddRowWithCollapse}
            disabled={isMaxJournalReached}
            className="flex items-center text-sm font-medium text-blue-600 disabled:cursor-not-allowed disabled:text-gray-400"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            {t("transactions.table.footer.addJournal")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JournalEntryTable;
